import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import documentsManifest from '../data/documentsManifest.json';
import { mapDocumentKeyToTypes } from '../utils/documentTypeMapper';
import type {
  DocumentManifest,
  ProjectDocument,
  DocumentCompletion,
  DocumentStatus
} from '../types/documents';

interface DocumentTypeMapping {
  category: string;
  key: string;
}

function mapDocumentTypeToManifest(documentType: string): DocumentTypeMapping {
  const typeUpper = documentType.toUpperCase();

  switch (typeUpper) {
    case 'KBIS':
      return { category: 'IDENT', key: 'KBIS' };
    case 'RIB':
      return { category: 'FIN', key: 'RIB' };
    case 'DESCRIPTION':
      return { category: 'PROJET', key: 'NOTE_PROJET' };
    case 'STATUTS':
      return { category: 'IDENT', key: 'STATUTS' };
    case 'JOAFE':
      return { category: 'IDENT', key: 'JOAFE' };
    case 'PV_AG_CA':
      return { category: 'IDENT', key: 'PV_AG_CA' };
    case 'BILAN':
      return { category: 'FIN', key: 'BILAN' };
    case 'BUDGET_PREV':
      return { category: 'FIN', key: 'BUDGET_PREV' };
    case 'PLAN_FIN':
      return { category: 'FIN', key: 'PLAN_FIN' };
    case 'URSSAF':
      return { category: 'LEGAL', key: 'URSSAF' };
    case 'FISCAL':
      return { category: 'LEGAL', key: 'FISCAL' };
    case 'ASSURANCE_RC':
      return { category: 'LEGAL', key: 'ASSURANCE_RC' };
    case 'RBE':
      return { category: 'LEGAL', key: 'RBE' };
    case 'ID_DIRIGEANT':
      return { category: 'LEGAL', key: 'ID_DIRIGEANT' };
    case 'NOTE_PROJET':
      return { category: 'PROJET', key: 'NOTE_PROJET' };
    case 'BUSINESS_PLAN':
      return { category: 'PROJET', key: 'BUSINESS_PLAN' };
    case 'CALENDRIER':
      return { category: 'PROJET', key: 'CALENDRIER' };
    case 'ETUDE_FAIS':
      return { category: 'PROJET', key: 'ETUDE_FAIS' };
    case 'EFFECTIF':
      return { category: 'RH', key: 'EFFECTIF' };
    case 'REG_INTERIEUR':
      return { category: 'RH', key: 'REG_INTERIEUR' };
    case 'ACCORDS':
      return { category: 'RH', key: 'ACCORDS' };
    case 'DOMICILIATION':
      return { category: 'SPEC', key: 'DOMICILIATION' };
    case 'DEVIS_FACTURES':
      return { category: 'SPEC', key: 'DEVIS_FACTURES' };
    case 'CERTIF':
      return { category: 'SPEC', key: 'CERTIF' };
    case 'VISUELS':
      return { category: 'SPEC', key: 'VISUELS' };
    default:
      return { category: 'SPEC', key: typeUpper };
  }
}

function findManifestItem(category: string, key: string) {
  const cat = documentsManifest.categories.find(c => c.code === category);
  if (!cat) return null;
  return cat.items.find(item => item.key === key);
}

interface DocumentState {
  manifest: DocumentManifest;
  documents: ProjectDocument[];
  completion: DocumentCompletion | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchDocuments: (projectId: string) => Promise<void>;
  uploadDocument: (projectId: string, categoryCode: string, key: string, file: File) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  verifyDocument: (documentId: string, status: DocumentStatus, note?: string) => Promise<void>;
  verifyAllPendingDocuments: (projectId: string) => Promise<void>;
  calculateCompletion: () => void;
  submitForVerification: (projectId: string) => Promise<void>;
}

export const useDocumentStore = create<DocumentState>((set, get) => ({
  manifest: documentsManifest,
  documents: [],
  completion: null,
  isLoading: false,
  error: null,

  fetchDocuments: async (projectId: string) => {
    if (!isSupabaseConfigured()) {
      set({ 
        documents: [], 
        completion: { requiredTotal: 0, requiredDone: 0, optionalDone: 0, percent: 0 },
        isLoading: false, 
        error: 'Supabase n\'est pas configuré. Veuillez configurer la connexion.' 
      });
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedDocuments: ProjectDocument[] = (data || []).map(doc => {
        // Use document_subtype (new system) or fallback to document_type (old system)
        const typeToMap = doc.document_subtype || doc.document_type;
        const mapping = mapDocumentTypeToManifest(typeToMap);
        const manifestItem = findManifestItem(mapping.category, mapping.key);
        const category = documentsManifest.categories.find(c => c.code === mapping.category);

        return {
          id: doc.id,
          project_id: doc.project_id,
          category: mapping.category,
          key: mapping.key,
          label: manifestItem?.label || doc.filename,
          file_url: doc.file_url,
          filename: doc.filename,
          mime_type: doc.mime_type,
          file_size: doc.file_size,
          status: doc.is_validated === true ? 'VERIFIED' :
                  doc.is_validated === false ? 'REJECTED' :
                  'UPLOADED',
          note: doc.validation_note || null,
          uploaded_by: doc.uploaded_by,
          uploaded_at: doc.created_at,
          verified_by: doc.validated_by,
          verified_at: doc.validated_at,
          required: manifestItem?.required ?? category?.required ?? false,
          conditional: manifestItem?.conditional ?? false
        };
      });

      set({ documents: transformedDocuments, isLoading: false });
      get().calculateCompletion();
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  uploadDocument: async (projectId: string, categoryCode: string, key: string, file: File) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    set({ isLoading: true, error: null });
    try {
      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${projectId}/${categoryCode}_${key}_${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('project-documents')
        .getPublicUrl(fileName);

      // Check if document already exists
      const existingDoc = get().documents.find(
        doc => doc.project_id === projectId && doc.category === categoryCode && doc.key === key
      );

      if (existingDoc) {
        // Update existing document
        const { error: updateError } = await supabase
          .from('documents')
          .update({
            filename: file.name,
            file_url: publicUrl,
            file_size: file.size,
            mime_type: file.type,
            is_validated: null,
            validated_by: null,
            validated_at: null
          })
          .eq('id', existingDoc.id);

        if (updateError) throw updateError;
      } else {
        // Create new document
        const { data: { user } } = await supabase.auth.getUser();

        // Map document key to type/subtype
        const { documentType, documentSubtype } = mapDocumentKeyToTypes(key);

        const { error: insertError } = await supabase
          .from('documents')
          .insert({
            project_id: projectId,
            filename: file.name,
            file_url: publicUrl,
            file_size: file.size,
            mime_type: file.type,
            document_type: documentType,
            document_subtype: documentSubtype,
            uploaded_by: user?.id,
            is_validated: null
          });

        if (insertError) throw insertError;
      }

      // Refresh documents
      await get().fetchDocuments(projectId);

      // Get project details for notifications
      const { data: project } = await supabase
        .from('projects')
        .select('*, owner:users!projects_owner_id_fkey(*), assigned_advisor:users!projects_assigned_advisor_id_fkey(*)')
        .eq('id', projectId)
        .maybeSingle();

      if (project) {
        // Notify coordinator and advisor about new document upload
        const { data: { user } } = await supabase.auth.getUser();
        const notificationRecipients: string[] = [];

        // Add assigned advisor if exists
        if (project.assigned_advisor_id) {
          notificationRecipients.push(project.assigned_advisor_id);
        }

        // Find coordinators to notify
        const { data: coordinators } = await supabase
          .from('users')
          .select('id')
          .eq('profile_type', 'COORDINATEUR');

        if (coordinators) {
          notificationRecipients.push(...coordinators.map(c => c.id));
        }

        // Create notifications
        if (notificationRecipients.length > 0) {
          const notificationInserts = notificationRecipients
            .filter(id => id !== user?.id) // Don't notify uploader
            .map(userId => ({
              user_id: userId,
              type: 'DOCUMENT_UPLOADED',
              title: 'Nouveau document téléversé',
              message: `Le porteur ${project.owner?.full_name || project.owner?.email} a téléversé un nouveau document pour le projet "${project.name}".`,
              link: `/projects/${projectId}/documents`,
              metadata: {
                project_id: projectId,
                project_name: project.name,
                document_type: key,
                uploaded_by: user?.id
              }
            }));

          if (notificationInserts.length > 0) {
            await supabase
              .from('notifications_enhanced')
              .insert(notificationInserts);
          }
        }
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteDocument: async (documentId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      // Update local state
      const { documents } = get();
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      set({ documents: updatedDocuments, isLoading: false });
      get().calculateCompletion();
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  verifyDocument: async (documentId: string, status: DocumentStatus, note?: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }

    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Get document details
      const { data: document } = await supabase
        .from('documents')
        .select('*, project:projects!documents_project_id_fkey(*, owner:users!projects_owner_id_fkey(*))')
        .eq('id', documentId)
        .maybeSingle();

      const isVerified = status === DocumentStatus.VERIFIED || status === 'VERIFIED';

      const { error } = await supabase
        .from('documents')
        .update({
          is_validated: isVerified,
          validated_by: user?.id,
          validated_at: new Date().toISOString(),
          validation_note: note || null
        })
        .eq('id', documentId);

      if (error) throw error;

      // Create notification for project owner
      if (document && document.project && document.project.owner_id) {
        const statusText = isVerified ? 'validé' : 'rejeté';
        let message = `Votre document "${document.filename}" pour le projet "${document.project.name}" a été ${statusText}.`;

        if (note && note.trim()) {
          message += ` Note: ${note}`;
        }

        await supabase
          .from('notifications_enhanced')
          .insert({
            user_id: document.project.owner_id,
            type: isVerified ? 'DOCUMENT_VALIDATED' : 'DOCUMENT_REJECTED',
            title: `Document ${statusText}`,
            message: message,
            link: `/projects/${document.project_id}/documents`,
            metadata: {
              project_id: document.project_id,
              project_name: document.project.name,
              document_id: documentId,
              document_filename: document.filename,
              status: status,
              validation_note: note,
              validated_by: user?.id
            }
          });
      }

      // Update local state
      const { documents } = get();
      const updatedDocuments = documents.map(doc =>
        doc.id === documentId
          ? {
              ...doc,
              status,
              note,
              verified_by: user?.id,
              verified_at: new Date().toISOString()
            }
          : doc
      );

      set({ documents: updatedDocuments, isLoading: false });
      get().calculateCompletion();
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  verifyAllPendingDocuments: async (projectId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }

    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Get all pending documents
      const { documents } = get();
      const pendingDocuments = documents.filter(d => d.status === 'UPLOADED');

      if (pendingDocuments.length === 0) {
        set({ isLoading: false });
        return;
      }

      // Update all pending documents (where is_validated is null)
      const { error } = await supabase
        .from('documents')
        .update({
          is_validated: true,
          validated_by: user?.id,
          validated_at: new Date().toISOString()
        })
        .eq('project_id', projectId)
        .is('is_validated', null);

      if (error) throw error;

      // Get project details
      const { data: project } = await supabase
        .from('projects')
        .select('*, owner:users!projects_owner_id_fkey(*)')
        .eq('id', projectId)
        .maybeSingle();

      // Create notification for project owner
      if (project && project.owner_id) {
        await supabase
          .from('notifications_enhanced')
          .insert({
            user_id: project.owner_id,
            type: 'DOCUMENTS_VALIDATED',
            title: 'Documents validés',
            message: `Tous vos documents en attente (${pendingDocuments.length}) pour le projet "${project.name}" ont été validés.`,
            link: `/projects/${projectId}/documents`,
            metadata: {
              project_id: projectId,
              project_name: project.name,
              document_count: pendingDocuments.length,
              validated_by: user?.id
            }
          });
      }

      // Refresh documents
      await get().fetchDocuments(projectId);

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  calculateCompletion: () => {
    const { manifest, documents } = get();
    
    // Get all required items from manifest
    const requiredItems = manifest.categories
      .filter(cat => cat.required)
      .flatMap(cat => cat.items.filter(item => item.required && !item.conditional));
    
    const requiredTotal = requiredItems.length;
    
    // Count completed required documents
    const requiredDone = requiredItems.filter(item => {
      const doc = documents.find(d => d.key === item.key);
      return doc && ['UPLOADED', 'VERIFIED'].includes(doc.status);
    }).length;
    
    // Count optional documents
    const optionalItems = manifest.categories
      .flatMap(cat => cat.items.filter(item => !item.required || cat.required === false));
    
    const optionalDone = optionalItems.filter(item => {
      const doc = documents.find(d => d.key === item.key);
      return doc && ['UPLOADED', 'VERIFIED'].includes(doc.status);
    }).length;
    
    const percent = requiredTotal > 0 ? Math.round((requiredDone / requiredTotal) * 100) : 100;
    
    set({
      completion: {
        requiredTotal,
        requiredDone,
        optionalDone,
        percent
      }
    });
  },

  submitForVerification: async (projectId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    set({ isLoading: true, error: null });
    try {
      // Update project status to indicate documents submitted for verification
      const { error } = await supabase
        .from('projects')
        .update({ 
          status: 'SOUMIS',
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (error) throw error;

      // Create notification for advisors
      // This would typically be handled by a trigger or separate API call
      
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  }
}));